import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import {
  InfoCard,
  Progress,
  StatusOK,
  StatusError,
  StatusWarning,
  WarningPanel,
  EmptyState,
} from '@backstage/core-components';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';

/**
 * Optional annotation to override which sloth_service the entity maps to.
 * Defaults to the entity name, which matches the CBWS convention
 * (e.g. component cbws/core-compute <-> sloth_service="core-compute").
 */
export const SLOTH_SERVICE_ANNOTATION = 'sloth.dev/service';

const GRAFANA_SLO_DASHBOARD =
  'https://dashboard.monitoring.cloudbear.it/d/ff5s5toexx4w0e/service-overview';

export const isSlothAvailable = (entity: Entity) =>
  entity.kind === 'Component' && (entity.spec as any)?.type === 'service';

const slothService = (entity: Entity) =>
  entity.metadata.annotations?.[SLOTH_SERVICE_ANNOTATION] ??
  entity.metadata.name;

type PromResult = {
  metric: Record<string, string>;
  value: [number, string];
};

type SloRow = {
  id: string;
  name: string;
  /** SLO objective in percent, from sloth_slo_info (e.g. 99). */
  objective?: number;
  /** Error budget remaining for the SLO period, 1 = untouched. */
  budgetRemaining?: number;
  /** Success ratio over the last 24h, 1 = no errors. */
  success24h?: number;
  /** Burn rate over the last 24h; > 1 exceeds the allowed error rate. */
  burn24h?: number;
};

const finite = (raw: string): number | undefined => {
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const useSlos = (service: string) => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  return useAsync(async (): Promise<SloRow[]> => {
    const baseUrl = `${await discoveryApi.getBaseUrl('proxy')}/slo`;
    const query = async (expr: string): Promise<PromResult[]> => {
      const response = await fetch(
        `${baseUrl}/api/v1/query?${new URLSearchParams({ query: expr })}`,
      );
      if (!response.ok) {
        throw new Error(
          `SLO query failed with ${response.status} ${response.statusText}`,
        );
      }
      const body = await response.json();
      return body?.data?.result ?? [];
    };

    const selector = `{sloth_service="${service}"}`;
    const [info, budget, error24h] = await Promise.all([
      query(`sloth_slo_info${selector}`),
      query(`slo:period_error_budget_remaining:ratio${selector}`),
      query(`slo:sli_error:ratio_rate1d${selector}`),
    ]);

    const rows = new Map<string, SloRow>();
    const upsert = (item: PromResult): SloRow => {
      const id = item.metric.sloth_id;
      const row = rows.get(id) ?? { id, name: item.metric.sloth_slo ?? id };
      rows.set(id, row);
      return row;
    };

    for (const item of info) {
      upsert(item).objective = finite(item.metric.sloth_objective);
    }
    // Sloth recording rules can yield NaN (e.g. incomplete 30d windows or
    // 0/0 on windows without traffic); treat non-finite values as absent.
    for (const item of budget) {
      upsert(item).budgetRemaining = finite(item.value[1]);
    }
    for (const item of error24h) {
      const row = upsert(item);
      const errorRatio = finite(item.value[1]);
      if (errorRatio === undefined) {
        continue;
      }
      row.success24h = 1 - errorRatio;
      if (row.objective !== undefined && row.objective < 100) {
        row.burn24h = errorRatio / (1 - row.objective / 100);
      }
    }

    return [...rows.values()].sort((a, b) => a.id.localeCompare(b.id));
  }, [service]);
};

const SloStatus = ({ row }: { row: SloRow }) => {
  // The period error budget is the real verdict when available.
  if (row.budgetRemaining !== undefined) {
    if (row.budgetRemaining <= 0) {
      return <StatusError>Budget exhausted</StatusError>;
    }
    if (row.burn24h !== undefined && row.burn24h > 1) {
      return <StatusWarning>Burning budget</StatusWarning>;
    }
    return <StatusOK>Meeting SLO</StatusOK>;
  }
  // Fall back to the last 24h against the objective.
  if (row.success24h !== undefined && row.objective !== undefined) {
    if (row.success24h < row.objective / 100) {
      return <StatusError>Violating (24h)</StatusError>;
    }
    return <StatusOK>Meeting (24h)</StatusOK>;
  }
  return <StatusWarning>No data</StatusWarning>;
};

const asPercent = (ratio?: number, digits = 1) =>
  ratio === undefined ? '—' : `${(ratio * 100).toFixed(digits)}%`;

const asBurnRate = (rate?: number) =>
  rate === undefined ? '—' : `${rate.toFixed(2)}x`;

export const EntitySloCard = () => {
  const { entity } = useEntity();
  const service = slothService(entity);
  const { value: rows, loading, error } = useSlos(service);

  const deepLink = {
    title: 'View in Grafana',
    link: `${GRAFANA_SLO_DASHBOARD}?orgId=8&${new URLSearchParams({
      'var-service': service,
    })}`,
  };

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel
        title="Could not load SLOs"
        message="Querying the SLO metrics failed. If this is a fresh setup, the /slo proxy credentials (SLO_BASIC_AUTH) are probably not configured."
      >
        {error.message}
      </WarningPanel>
    );
  } else if (!rows || rows.length === 0) {
    content = (
      <EmptyState
        title={`No SLOs found for ${service}`}
        missing="data"
        description="No Sloth SLO recording rules matched this service. Define SLOs for it, or set the sloth.dev/service annotation if the sloth_service label differs from the entity name."
      />
    );
  } else {
    content = (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>SLO</TableCell>
            <TableCell>Objective</TableCell>
            <TableCell>Success (24h)</TableCell>
            <TableCell>Burn (24h)</TableCell>
            <TableCell>Budget left</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(row => (
            <TableRow key={row.id}>
              <TableCell>
                <Typography variant="body2">{row.name}</Typography>
              </TableCell>
              <TableCell>
                {row.objective !== undefined ? `${row.objective}%` : '—'}
              </TableCell>
              <TableCell>{asPercent(row.success24h, 3)}</TableCell>
              <TableCell>{asBurnRate(row.burn24h)}</TableCell>
              <TableCell>{asPercent(row.budgetRemaining)}</TableCell>
              <TableCell>
                <SloStatus row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <InfoCard title="SLOs" deepLink={deepLink}>
      {content}
    </InfoCard>
  );
};
